"use client";

import React from "react";
import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import DataTable from "@/components/ui/DataTable";
import SearchableSelect from "@/components/ui/SearchableSelect";
import DatePicker from "@/components/form/date-picker";
import Pagination from "@/components/tables/Pagination";
import DrawerPanel from "@/components/ui/DrawerPanel";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import { Modal } from "@/components/ui/modal/index";
import { TrashBinIcon } from "@/icons";
import { handleApiError } from "@/lib/api";
import { rmcClient } from "@/lib/rmcClient";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

const PAGE_SIZE = 20;

export default function RmcPurchasePayments() {
  const qc = useQueryClient();
  const { isoToday, isoThreeDaysAgo } = React.useMemo(() => {
    const now = new Date();
    const fromDate = new Date(now);
    fromDate.setDate(fromDate.getDate() - 3);
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    const fromY = fromDate.getFullYear();
    const fromM = String(fromDate.getMonth() + 1).padStart(2, "0");
    const fromD = String(fromDate.getDate()).padStart(2, "0");
    return {
      isoToday: `${y}-${m}-${d}`,
      isoThreeDaysAgo: `${fromY}-${fromM}-${fromD}`,
    };
  }, []);
  const [filters, setFilters] = React.useState({
    date_from: isoThreeDaysAgo,
    date_to: isoToday,
    purchase_party_id: "",
  });
  const [page, setPage] = React.useState(1);
  const [isExporting, setIsExporting] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [deleteRow, setDeleteRow] = React.useState(null);
  const [form, setForm] = React.useState({
    purchase_party_id: "",
    date: new Date().toISOString().slice(0, 10),
    amount: "",
    remark: "",
  });

  const { data: purchaseParties = [] } = useQuery({
    queryKey: ["purchaseParties"],
    queryFn: async () => (await rmcClient.listPurchaseParties()).data,
  });
  const queryParams = React.useMemo(
    () => ({
      ...(filters.purchase_party_id ? { purchase_party_id: filters.purchase_party_id } : {}),
      ...(filters.date_from ? { date_from: filters.date_from } : {}),
      ...(filters.date_to ? { date_to: filters.date_to } : {}),
      page,
      page_size: PAGE_SIZE,
    }),
    [filters, page]
  );
  const { data: paymentsData, isFetching } = useQuery({
    queryKey: ["purchasePayments", queryParams],
    queryFn: async () => (await rmcClient.listPurchasePayments(queryParams)).data,
    keepPreviousData: true,
  });
  const rows = React.useMemo(() => {
    if (Array.isArray(paymentsData)) return paymentsData;
    if (Array.isArray(paymentsData?.results)) return paymentsData.results;
    if (Array.isArray(paymentsData?.data?.results)) return paymentsData.data.results;
    return [];
  }, [paymentsData]);
  const totalCount = React.useMemo(() => {
    const value = Number(paymentsData?.count);
    if (Number.isFinite(value) && value >= 0) return value;
    const nested = Number(paymentsData?.data?.count);
    if (Number.isFinite(nested) && nested >= 0) return nested;
    return rows.length;
  }, [paymentsData, rows.length]);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const activeFilterCount = React.useMemo(
    () => [filters.date_from, filters.date_to, filters.purchase_party_id].filter(Boolean).length,
    [filters]
  );

  const save = useMutation({
    mutationFn: () =>
      rmcClient.createPurchasePayment({
        purchase_party_id: Number(form.purchase_party_id),
        date: form.date,
        amount: form.amount,
        remark: form.remark || "",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["purchasePayments"] });
      toast.success("Payment created");
      setOpen(false);
      setForm({
        purchase_party_id: "",
        date: new Date().toISOString().slice(0, 10),
        amount: "",
        remark: "",
      });
    },
    onError: (e) => toast.error(handleApiError(e)),
  });

  const remove = useMutation({
    mutationFn: (id) => rmcClient.deletePurchasePayment(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["purchasePayments"] });
      toast.success("Payment deleted");
      setDeleteRow(null);
    },
    onError: (e) => toast.error(handleApiError(e)),
  });

  const partyName = React.useCallback(
    (id) => purchaseParties.find((p) => p.id === id)?.name || "—",
    [purchaseParties]
  );
  const updateFilters = React.useCallback((next) => {
    setFilters((prev) => ({ ...prev, ...next }));
    setPage(1);
  }, []);
  const clearFilters = React.useCallback(() => {
    setFilters({
      date_from: isoThreeDaysAgo,
      date_to: isoToday,
      purchase_party_id: "",
    });
    setPage(1);
  }, [isoThreeDaysAgo, isoToday]);
  const exportFiltered = React.useCallback(async () => {
    setIsExporting(true);
    try {
      const payload = (
        await rmcClient.listPurchasePayments({
          ...(filters.purchase_party_id ? { purchase_party_id: filters.purchase_party_id } : {}),
          ...(filters.date_from ? { date_from: filters.date_from } : {}),
          ...(filters.date_to ? { date_to: filters.date_to } : {}),
          pagination: false,
        })
      ).data;
      const exportRows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.results)
          ? payload.results
          : Array.isArray(payload?.data?.results)
            ? payload.data.results
            : [];
      const header = ["date", "purchase_party", "amount", "remark"];
      const lines = exportRows.map((r) =>
        [r.date ?? "", partyName(r.purchase_party_id), r.amount ?? "", r.remark ?? ""]
          .map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`)
          .join(",")
      );
      const csv = [header.join(","), ...lines].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `purchase-payments-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${exportRows.length} payments`);
    } catch {
      toast.error("Failed to export payments.");
    } finally {
      setIsExporting(false);
    }
  }, [filters, partyName]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <PageBreadCrumb pageTitle="Payments" />
        <div className="flex items-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={isExporting}
            onClick={exportFiltered}
          >
            {isExporting ? "Exporting…" : "Export All Filtered"}
          </Button>
          <Button size="sm" onClick={() => setOpen(true)}>
            Add Payment
          </Button>
        </div>
      </div>
      <div className="rounded-2xl border border-gray-200/80 bg-white/90 p-4 shadow-lg shadow-gray-200/50 ring-1 ring-gray-100/80 backdrop-blur-sm transition-all dark:border-gray-800 dark:bg-gray-900/50 dark:shadow-black/30 dark:ring-gray-800/80">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
              Smart Filters
            </p>
            {activeFilterCount > 0 && (
              <span className="menu-dropdown-badge-active rounded-full px-2.5 py-1 text-[11px] font-semibold">
                {activeFilterCount} active
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Showing {rows.length} of {totalCount} records
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Date From</label>
            <DatePicker
              id="payment-filter-from"
              defaultDate={filters.date_from}
              placeholder="From date"
              onChange={(_, dateStr) => updateFilters({ date_from: dateStr || "" })}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">Date To</label>
            <DatePicker
              id="payment-filter-to"
              defaultDate={filters.date_to}
              placeholder="To date"
              onChange={(_, dateStr) => updateFilters({ date_to: dateStr || "" })}
            />
          </div>
          <SearchableSelect
            label="Purchase Party"
            value={filters.purchase_party_id}
            onChange={(v) => updateFilters({ purchase_party_id: v || "" })}
            options={[{ id: "", name: "All parties" }, ...purchaseParties]}
            getLabel={(o) => o.name}
            getValue={(o) => o.id}
            getSubLabel={(p) => p.gst_number}
            placeholder="All parties"
            overlayZClass="z-[100001]"
            menuZClass="z-[100002]"
          />
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2 border-t border-gray-200/70 pt-3 dark:border-gray-800">
          <Button type="button" size="sm" variant="outline" onClick={clearFilters}>
            Clear Filters
          </Button>
        </div>
      </div>

      <DataTable
        columns={[
          { key: "date", header: "Date" },
          { key: "party", header: "Purchase Party", render: (r) => partyName(r.purchase_party_id) },
          { key: "amount", header: "Amount", render: (r) => `₹${Number(r.amount || 0).toFixed(2)}` },
          { key: "remark", header: "Remark", render: (r) => r.remark || "—" },
          {
            key: "act",
            header: "",
            headerClassName: "text-right",
            cellClassName: "text-right",
            render: (r) => (
              <div className="flex justify-end">
                <button
                  type="button"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-red-50 hover:text-red-600 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteRow(r);
                  }}
                >
                  <TrashBinIcon className="size-4" />
                </button>
              </div>
            ),
          },
        ]}
        data={rows}
        isLoading={isFetching}
      />
      <div className="flex justify-end">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(p) => setPage(Math.max(1, Math.min(totalPages, p)))}
        />
      </div>

      <DrawerPanel open={open} onClose={() => setOpen(false)} title="Add Payment" widthClass="max-w-2xl">
        <div className="space-y-3">
          <SearchableSelect
            label="Purchase Party"
            value={form.purchase_party_id}
            onChange={(v) => setForm((p) => ({ ...p, purchase_party_id: v || "" }))}
            options={purchaseParties}
            getSubLabel={(p) => p.gst_number}
            overlayZClass="z-[100002]"
            menuZClass="z-[100003]"
          />
          <div>
            <Label>Date</Label>
            <Input className="mt-1" type="date" value={form.date} onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))} />
          </div>
          <div>
            <Label>Amount</Label>
            <Input className="mt-1" type="number" step="0.01" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} />
          </div>
          <div>
            <Label>Remark</Label>
            <Input className="mt-1" value={form.remark} onChange={(e) => setForm((p) => ({ ...p, remark: e.target.value }))} />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending || !form.purchase_party_id || !form.amount || !form.date}>
            {save.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      </DrawerPanel>

      <Modal isOpen={deleteRow != null} onClose={() => !remove.isPending && setDeleteRow(null)} className="max-w-md w-full">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Delete payment?</h3>
          <p className="text-sm text-gray-400">This will permanently delete the selected purchase payment.</p>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" disabled={remove.isPending} onClick={() => setDeleteRow(null)}>Cancel</Button>
            <Button size="sm" disabled={remove.isPending} onClick={() => deleteRow?.id && remove.mutate(deleteRow.id)}>
              {remove.isPending ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
